import { ReloadOutlined } from '@ant-design/icons';
import type { StockAlertRow, StockAlertSummary } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { hq } from '../api/client';
import { sumWidth } from '@hq/ui-kit';

const PAGE_SIZE = 20;

const STATUS_META: Record<string, { text: string; color: string }> = {
  NEW: { text: '待处理', color: 'red' },
  PROCESSING: { text: '处理中', color: 'gold' },
  RESOLVED: { text: '已解决', color: 'green' },
  IGNORED: { text: '已忽略', color: 'default' }
};

/** 预警类型：低库存/缺货、近效期/过期、高储、呆滞、应急储备不足（由 cfg_stock_rule 各阈值列驱动）。 */
const ALERT_TYPE_META: Record<string, { text: string; color: string }> = {
  LOW_STOCK: { text: '低库存', color: 'red' },
  STOCK_OUT: { text: '缺货', color: 'red' },
  NEAR_EXPIRY: { text: '近效期', color: 'orange' },
  EXPIRED: { text: '已过期', color: 'volcano' },
  OVER_STOCK: { text: '高储', color: 'gold' },
  DULL_STOCK: { text: '呆滞', color: 'purple' },
  EMERGENCY_STOCK: { text: '应急储备不足', color: 'magenta' }
};

const LEVEL_META: Record<string, { text: string; color: string }> = {
  HIGH: { text: '高', color: 'red' },
  MIDDLE: { text: '中', color: 'gold' },
  LOW: { text: '低', color: 'blue' }
};

/** 库存/效期预警：待处理 → 处理中 → 已解决闭环（演-5）。 */
export default function StockAlertList() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<string>('NEW');
  const [alertType, setAlertType] = useState<string>('');
  const [alertLevel, setAlertLevel] = useState<string>('');
  const [warehouseId, setWarehouseId] = useState<string>('');
  const [scanDays, setScanDays] = useState(30);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [desc, setDesc] = useState('');
  const [closingId, setClosingId] = useState<string | null>(null);
  const [closeResult, setCloseResult] = useState<'RESOLVED' | 'IGNORED'>('RESOLVED');

  const summary = useQuery({
    queryKey: ['stock-alert', 'summary'],
    queryFn: () => hq.stockAlert.summary()
  });

  const warehouses = useQuery({
    queryKey: ['org-structure', 'warehouse', 'options'],
    queryFn: () => hq.orgStructure.warehouse.list(),
    staleTime: 5 * 60 * 1000
  });

  // 游标分页：后端支持 lastId，避免「加载更多」时整页重取
  const list = useInfiniteQuery({
    queryKey: ['stock-alert', 'list', status, alertType, alertLevel, warehouseId],
    placeholderData: keepPreviousData,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hq.stockAlert.list({
        lastId: pageParam ?? undefined,
        size: PAGE_SIZE,
        alertStatus: status || undefined,
        alertType: alertType || undefined,
        alertLevel: alertLevel || undefined,
        warehouseId: warehouseId || undefined
      }),
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextLastId : undefined)
  });

  const scan = useMutation({
    mutationFn: (days: number) => hq.stockAlert.scan(days),
    onSuccess: async (result) => {
      message.success(
        `扫描完成：库存 ${result.lowStock} 条 / 效期 ${result.expiry} 条 / 高储 ${result.overStock ?? 0} 条 / ` +
          `呆滞 ${result.dull ?? 0} 条 / 应急储备 ${result.emergency ?? 0} 条，写入 ${result.upserted} 条`
      );
      await queryClient.invalidateQueries({ queryKey: ['stock-alert'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '扫描失败')
  });

  const process = useMutation({
    mutationFn: (id: string) => hq.stockAlert.process(id, 'ACK', desc || undefined),
    onSuccess: async () => {
      message.success('已转入处理中');
      setProcessingId(null);
      setDesc('');
      await queryClient.invalidateQueries({ queryKey: ['stock-alert'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '操作失败')
  });

  const close = useMutation({
    mutationFn: (id: string) => hq.stockAlert.close(id, closeResult, desc || undefined),
    onSuccess: async () => {
      message.success('预警已关闭');
      setClosingId(null);
      setDesc('');
      await queryClient.invalidateQueries({ queryKey: ['stock-alert'] });
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '操作失败')
  });

  const columns: ColumnsType<StockAlertRow> = [
    { title: '预警编号', dataIndex: 'alertCode', width: 190 },
    { title: '物资', width: 220, render: (_, row) => `${row.materialName ?? '-'}${row.spec ? ` / ${row.spec}` : ''}` },
    { title: '仓库', dataIndex: 'warehouseName', width: 140, render: (v: string | null) => v || '-' },
    {
      title: '类型',
      dataIndex: 'alertType',
      width: 110,
      render: (value: string) => {
        const meta = ALERT_TYPE_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '级别',
      dataIndex: 'alertLevel',
      width: 80,
      render: (value: string) => {
        const meta = LEVEL_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    {
      title: '触发值 / 阈值',
      width: 130,
      align: 'right',
      render: (_, row) => `${formatQty(row.triggerValue)} / ${formatQty(row.thresholdValue)}`
    },
    {
      title: '状态',
      dataIndex: 'alertStatus',
      width: 100,
      render: (value: string) => {
        const meta = STATUS_META[value] ?? { text: value, color: 'default' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '首次预警', dataIndex: 'firstAlertAt', width: 160, render: (v: string) => formatDisplayTime(v) },
    {
      title: '操作',
      width: 170,
      render: (_, row) => (
        <Space size={4}>
          {row.alertStatus === 'NEW' ? (
            <Button size="small" type="link" onClick={() => { setProcessingId(row.id); setDesc(''); }}>
              处理
            </Button>
          ) : null}
          {row.alertStatus === 'NEW' || row.alertStatus === 'PROCESSING' ? (
            <Button size="small" type="link" onClick={() => { setClosingId(row.id); setDesc(''); }}>
              关闭
            </Button>
          ) : null}
        </Space>
      )
    }
  ];

  const data = summary.data as StockAlertSummary | undefined;
  const rows = list.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col flex="1 1 170px">
          <Card size="small">
            <Statistic title="未闭环预警" value={data?.total ?? 0} loading={summary.isPending} />
          </Card>
        </Col>
        <Col flex="1 1 170px">
          <Card size="small">
            <Statistic title="高等级" value={data?.byLevel?.HIGH ?? 0} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
        <Col flex="1 1 170px">
          <Card size="small">
            <Statistic title="库存不足" value={(data?.byType?.LOW_STOCK ?? 0) + (data?.byType?.STOCK_OUT ?? 0)} />
          </Card>
        </Col>
        <Col flex="1 1 170px">
          <Card size="small">
            <Statistic title="效期预警" value={(data?.byType?.NEAR_EXPIRY ?? 0) + (data?.byType?.EXPIRED ?? 0)} />
          </Card>
        </Col>
        <Col flex="1 1 170px">
          <Card size="small">
            <Statistic title="高储预警" value={data?.byType?.OVER_STOCK ?? 0} />
          </Card>
        </Col>
        <Col flex="1 1 170px">
          <Card size="small">
            <Statistic title="呆滞预警" value={data?.byType?.DULL_STOCK ?? 0} />
          </Card>
        </Col>
        <Col flex="1 1 170px">
          <Card size="small">
            <Statistic title="应急储备不足" value={data?.byType?.EMERGENCY_STOCK ?? 0} valueStyle={{ color: '#cf1322' }} />
          </Card>
        </Col>
      </Row>

      <Card
        className="page-card"
        title="库存预警"
        extra={
          <Space wrap>
            <Select
              value={status}
              style={{ width: 120 }}
              onChange={setStatus}
              options={[
                { value: 'NEW', label: '待处理' },
                { value: 'PROCESSING', label: '处理中' },
                { value: 'RESOLVED', label: '已解决' },
                { value: '', label: '全部' }
              ]}
            />
            <Select
              value={alertType}
              style={{ width: 110 }}
              onChange={setAlertType}
              options={[
                { value: '', label: '全部类型' },
                { value: 'LOW_STOCK', label: '低库存' },
                { value: 'STOCK_OUT', label: '缺货' },
                { value: 'NEAR_EXPIRY', label: '近效期' },
                { value: 'EXPIRED', label: '已过期' },
                { value: 'OVER_STOCK', label: '高储' },
                { value: 'DULL_STOCK', label: '呆滞' },
                { value: 'EMERGENCY_STOCK', label: '应急储备不足' }
              ]}
            />
            <Select
              value={alertLevel}
              style={{ width: 100 }}
              onChange={setAlertLevel}
              options={[
                { value: '', label: '全部级别' },
                { value: 'HIGH', label: '高' },
                { value: 'MIDDLE', label: '中' },
                { value: 'LOW', label: '低' }
              ]}
            />
            <Select
              value={warehouseId}
              style={{ width: 170 }}
              showSearch
              optionFilterProp="label"
              onChange={setWarehouseId}
              options={[
                { value: '', label: '全部仓库' },
                ...(warehouses.data ?? []).map((row) => ({ value: row.id, label: `${row.whName}（${row.whCode}）` }))
              ]}
            />
            <InputNumber
              min={1}
              max={365}
              value={scanDays}
              onChange={(value) => setScanDays(Number(value ?? 30))}
              addonBefore="效期"
              addonAfter="天"
              style={{ width: 140 }}
            />
            <Button
              loading={scan.isPending}
              onClick={() =>
                modal.confirm({
                  title: '立即扫描预警规则',
                  content: `按当前库存与效期规则扫描，回看 ${scanDays} 天内的效期数据；重复执行只会更新未闭环预警。`,
                  okText: '开始扫描',
                  onOk: () => scan.mutateAsync(scanDays)
                })
              }
            >
              扫描规则
            </Button>
            <Button
              icon={<ReloadOutlined />}
              loading={list.isFetching}
              onClick={() => {
                void list.refetch();
                void summary.refetch();
              }}
            >
              刷新
            </Button>
          </Space>
        }
      >
        {list.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="预警加载失败"
            description={list.error instanceof ApiError ? list.error.friendlyMessage : String(list.error)}
          />
        ) : null}

        <Table<StockAlertRow>
          rowKey="id"
          size="small"
          loading={list.isPending}
          columns={columns}
          dataSource={rows}
          pagination={false}
          scroll={{ x: sumWidth(columns) }}
          locale={{ emptyText: '暂无预警' }}
        />

        <Space style={{ marginTop: 16 }}>
          <Typography.Text type="secondary">已加载 {rows.length} 条</Typography.Text>
          {list.hasNextPage ? (
            <Button onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>
              加载更多
            </Button>
          ) : null}
        </Space>
      </Card>

      <Modal
        title="转入处理中"
        open={processingId !== null}
        onCancel={() => setProcessingId(null)}
        onOk={() => processingId && process.mutate(processingId)}
        confirmLoading={process.isPending}
        okText="确认处理"
      >
        <Typography.Paragraph type="secondary">
          处理动作将写入处理记录（al_handle），预警状态推进为「处理中」，后续补货到货后可关闭。
        </Typography.Paragraph>
        <Input.TextArea
          rows={3}
          placeholder="处理说明，例如「已生成请购单，预计 2 日到货」"
          value={desc}
          onChange={(event) => setDesc(event.target.value)}
        />
      </Modal>

      <Modal
        title="关闭预警"
        open={closingId !== null}
        onCancel={() => setClosingId(null)}
        onOk={() => closingId && close.mutate(closingId)}
        confirmLoading={close.isPending}
        okText="确认关闭"
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Select
            value={closeResult}
            onChange={setCloseResult}
            style={{ width: '100%' }}
            options={[
              { value: 'RESOLVED', label: '已解决（补货到货 / 库存恢复）' },
              { value: 'IGNORED', label: '忽略（非真实缺货）' }
            ]}
          />
          <Input.TextArea
            rows={3}
            placeholder="处理结论"
            value={desc}
            onChange={(event) => setDesc(event.target.value)}
          />
        </Space>
      </Modal>
    </>
  );
}
